import React, { useEffect, useMemo, useState } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Table from "../../../ui-components/table/table";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import {
  ZIP_ROUTING_ACTIVITY_LOG_ACTION_OPTIONS,
  ZipRoutingProfileActivityLogFilters,
  ZipRoutingProfileActivityLogModel,
} from "../../../app/data/zip-routing-profile/models";
import { useZipRoutingProfileActivityLogList } from "../../../hooks/useZipRoutingProfiles";
import getRouteGuideActivityLogColumns from "./routeGuideActivityColumns";
import "./routeGuideActivityLog.scss";

export interface RouteGuideActivityLogProps {
  show: boolean;
  onClose: () => void;
}

const formatFilterDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length !== 3) return "";
  const [month, day, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const formatDescriptionLine = (line: string): string => {
  const updatedLine = line.replace(/-->/g, "→");
  return updatedLine.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\[UTC\]/g,
    (match) => {
      const clean = match.replace(/\[.*\]/, "");
      return typeof (clean as any).toUiMonthDateFormat === "function"
        ? (clean as any).toUiMonthDateFormat()
        : clean;
    }
  );
};

const RouteGuideActivityLog: React.FC<RouteGuideActivityLogProps> = (props) => {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [actionType, setActionType] = useState<XGSSelectOption | null | undefined>(null);
  const [appliedFilters, setAppliedFilters] = useState<ZipRoutingProfileActivityLogFilters>({});
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useZipRoutingProfileActivityLogList(appliedFilters, props.show);

  const actionOptions = useMemo(
    () =>
      ZIP_ROUTING_ACTIVITY_LOG_ACTION_OPTIONS.map(
        (action): XGSSelectOption => ({
          label: action.name,
          value: action.id,
        })
      ),
    []
  );

  const tableRows = useMemo(() => {
    const logs: ZipRoutingProfileActivityLogModel[] = data?.allItems || [];
    const rows: Array<{
      changedAt: string;
      zipLabel: string;
      terminalNumber: number | null;
      action: string;
      modifiedBy: string;
      changeDescription: string;
    }> = [];

    logs.forEach((item) => {
      const lines = (item.changeDescription || "")
        .split("\n")
        .filter((line) => line.trim() !== "");

      const zipPart = item.zipCode || "—";
      const statePart = item.state ? ` (${item.state})` : "";
      const terminalLabel =
        item.airportCode || item.terminalName
          ? ` — ${item.airportCode || item.terminalName}`
          : "";
      const zipLabel = `${zipPart}${statePart}${terminalLabel}`;

      if (lines.length === 0) {
        rows.push({
          changedAt: item.changedAt,
          zipLabel,
          terminalNumber: item.terminalNumber,
          action: item.action,
          modifiedBy: item.modifiedBy,
          changeDescription: "",
        });
        return;
      }

      lines.forEach((line) => {
        rows.push({
          changedAt: item.changedAt,
          zipLabel,
          terminalNumber: item.terminalNumber,
          action: item.action,
          modifiedBy: item.modifiedBy,
          changeDescription: formatDescriptionLine(line),
        });
      });
    });

    return rows;
  }, [data?.allItems]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!props.show) return;
    setAppliedFilters({
      search: debouncedSearch.trim() || undefined,
      action: actionType?.value || actionType?.label || null,
      dateFrom: fromDate || null,
      dateTo: toDate || null,
    });
  }, [debouncedSearch, actionType, fromDate, toDate, props.show]);

  const searchRecords = () => {
    setAppliedFilters({
      search: search.trim() || undefined,
      action: actionType?.value || actionType?.label || null,
      dateFrom: fromDate || null,
      dateTo: toDate || null,
    });
  };

  const clearSearchRecords = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setDebouncedSearch("");
    setActionType(null);
    setAppliedFilters({});
  };

  return (
    <SlideOutSidebar
      header="Route Guide Activity Log"
      onClose={props.onClose}
      show={props.show}
      size="medium"
      className="xgs-route-guide-activitylog"
    >
      <>
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search xgs-route-guide-activitylog__filters">
            <LabeledTextInput
              label="Search ZIP"
              labelMode={LabelModes.column}
              className="xgs-route-guide-activitylog__filters__input"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && !isLoading && searchRecords()}
              type="text"
              placeholder="Search across all fields"
            />
            <LabeledDateRangeInput
              label="Dates range:"
              labelMode={LabelModes.column}
              className="xgs-route-guide-activitylog__filters__dates"
              start={fromDate}
              end={toDate}
              onStartChange={(value) => setFromDate(formatFilterDate(value))}
              onEndChange={(value) => setToDate(formatFilterDate(value))}
            />
            <LabeledSelectInput
              label="Action"
              onValueChange={setActionType}
              name="actionType"
              value={actionType}
              className="xgs-route-guide-activitylog__filters__input"
              options={actionOptions}
              labelMode={LabelModes.column}
              placeholder="Action Type"
            />
            <Button
              className="xgs-route-guide-activitylog__filters__button"
              theme={ButtonThemes.gray}
              onClick={clearSearchRecords}
            >
              Clear Filters
            </Button>
            <Button
              className="xgs-route-guide-activitylog__filters__button xgs-route-guide-activitylog__filters__button--last"
              theme={ButtonThemes.blue}
              disabled={isLoading}
              onClick={searchRecords}
            >
              Search
            </Button>
          </div>
        </div>

        {isError && (
          <XGSErrorMessage>
            {(error as any)?.message || "Error while obtaining the activity log"}
          </XGSErrorMessage>
        )}

        {!isError && (
          <div className="xgs-route-guide-activitylog__table-wrapper">
            <Table
              isLoading={isLoading}
              columns={getRouteGuideActivityLogColumns()}
              data={tableRows}
              rowHeight={50}
              minTableHeight={200}
              noResultsText="There are no logs available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
              sorting={true}
              enableMultiSort={true}
            />
          </div>
        )}
      </>
    </SlideOutSidebar>
  );
};

export default RouteGuideActivityLog;
